import { ArrowDown, ArrowUp, Star, X } from 'lucide-react'
import { validerCheminFavori } from '@gestion/shared/favoris'
import { useToast } from './primitives.js'
import { deplacerFavori, iconeDeFavori, retirerFavori, useFavoris } from '../shell/favoris.js'

/**
 * PRÉFÉRENCES › MES FAVORIS (lot 5, 24/10/2026) — ranger les favoris de la
 * barre latérale : Monter, Descendre, Retirer.
 *
 * Hors de la Save Bar de la page : chaque geste s'applique tout de suite,
 * comme le mode de la barre, et se défait (« Annuler » après un retrait). Pas
 * de renommage (décision d'Hugo) : on retire, puis on épingle de nouveau.
 *
 * Une adresse que le validateur partagé refuse (règle durcie après coup)
 * n'apparaît plus dans la barre ; elle reste ici, marquée « Adresse refusée »,
 * pour qu'on puisse la retirer. Noms et adresses en texte, jamais en HTML.
 */
export function MesFavoris() {
  const { statut, liste, max } = useFavoris()
  const toast = useToast()
  const n = liste.length

  return (
    <section id="favoris" className="lc-panel gd-panel mf-panel" aria-labelledby="ap-favoris" style={{ marginTop: 'var(--sp-4)' }}>
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="ap-favoris"><Star size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Mes favoris</h2>
        <span className="lc-panel__spacer" />
        <span className="lc-pill lc-pill--idle" aria-label={`${n} ${n > 1 ? 'favoris' : 'favori'} sur ${max}`}>{n} / {max}</span>
      </header>
      <div className="lc-panel__body">
        <p className="mf-note">
          Vos favoris s’affichent en haut de la barre latérale. Ces changements s’appliquent tout de suite.
        </p>
        {statut === 'chargement' || statut === 'vide' ? (
          <p className="mf-vide">Lecture de vos favoris…</p>
        ) : statut === 'erreur' && liste.length === 0 ? (
          <p className="mf-vide">Favoris indisponibles pour l’instant.</p>
        ) : liste.length === 0 ? (
          <p className="mf-vide">
            Aucun favori pour l’instant. Ajoutez une page avec l’étoile en haut de l’écran, ou par un clic droit :
            « Ajouter aux favoris », dans les menus.
          </p>
        ) : (
          <ol className="mf-liste">
            {liste.map((f, i) => {
              const valide = validerCheminFavori(f.path).ok
              const Icone = iconeDeFavori(f.path)
              return (
                <li key={f.id} className="mf-ligne">
                  <Icone size={16} className="mf-ligne__icone" aria-hidden="true" />
                  <span className="mf-ligne__texte">
                    <span className="mf-ligne__nom">{f.label}</span>
                    <span className="mf-ligne__adresse">{f.path}</span>
                  </span>
                  {valide ? (
                    <>
                      <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon"
                        aria-label={`Monter « ${f.label} »`} title="Monter" disabled={i === 0}
                        onClick={() => { void deplacerFavori(f.id, -1, toast) }}>
                        <ArrowUp size={15} aria-hidden="true" />
                      </button>
                      <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon"
                        aria-label={`Descendre « ${f.label} »`} title="Descendre" disabled={i === liste.length - 1}
                        onClick={() => { void deplacerFavori(f.id, 1, toast) }}>
                        <ArrowDown size={15} aria-hidden="true" />
                      </button>
                    </>
                  ) : (
                    <span className="lc-pill lc-pill--warning">Adresse refusée</span>
                  )}
                  <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon"
                    aria-label={`Retirer « ${f.label} » des favoris`} title="Retirer des favoris"
                    onClick={() => { void retirerFavori(f, toast) }}>
                    <X size={15} aria-hidden="true" />
                  </button>
                </li>
              )
            })}
          </ol>
        )}
      </div>
    </section>
  )
}
